import { css } from '@compiled/react';

import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { formatIcimsLocation } from './utils/format-icims-location';

/** Bound Uniform values arrive wrapped as `{ value }`; authored ones arrive bare. */
type UniformValueParameter<T> = T | { value?: T };

type SubheadingAlignment = 'left' | 'center' | 'right';

/** The reference page divides every segment with a pipe. */
const SEGMENT_SEPARATOR = ' | ';

export interface UniformJobPostingSubheadingProps {
	/** `icimsJob.category`. Present on all 234 live jobs, with 11 distinct values. */
	readonly category?: UniformValueParameter<string>;
	/** `icimsJob.locations`, raw. Live jobs hold 0 to 7 entries, and 28 hold none. */
	readonly locations?: UniformValueParameter<readonly unknown[]>;
	/** `icimsJob.type`. Absent on 144 of 234 live jobs, and `Full-Time` on the rest. */
	readonly type?: UniformValueParameter<string>;
	readonly alignment?: UniformValueParameter<SubheadingAlignment>;
}

/**
 * The one-line subheading under a careers job title: the category, then every
 * location, then the type.
 *
 * Mirrors the live Magnolia component `imkt.components.JobPostingDetails`, whose
 * params are `postingTeam`, `postingLocation` and `postingCommitment`, which map to
 * `icimsJob.category`, `icimsJob.locations` and `icimsJob.type`. Its fourth param
 * `remoteEligible` is empty on every sampled job, so this omits it.
 *
 * One deliberate difference from the reference: Magnolia emits the separator
 * before `postingCommitment` unconditionally, so a job with no `type` renders a
 * dangling `|` (see job 25169). 144 of 234 live jobs carry no `type`, so that
 * defect shows on most job pages. This drops empty segments instead.
 */
const UniformJobPostingSubheading = ({
	category: rawCategory,
	locations: rawLocations,
	type: rawType,
	alignment: rawAlignment,
}: UniformJobPostingSubheadingProps): JSX.Element | null => {
	const alignment = (unwrapText(rawAlignment) ?? 'center') as SubheadingAlignment;

	const segments = [
		unwrapText(rawCategory),
		...unwrapLocations(rawLocations),
		unwrapText(rawType),
	].filter((segment): segment is string => segment !== undefined);

	// An unbound or closed job resolves to nothing. Render no empty paragraph.
	if (segments.length === 0) {
		return null;
	}

	const alignmentStyle = css({
		textAlign: alignment,
	});

	return (
		<TypographyStyles>
			<p className="body-lg" css={[resetMargin, alignmentStyle, colorStyle]}>
				{segments.join(SEGMENT_SEPARATOR)}
			</p>
		</TypographyStyles>
	);
};

const resetMargin = css({
	margin: 0,
});

const colorStyle = css({
	color: 'var(--wac-color-body)',
});

function unwrapText(parameter: UniformValueParameter<string> | undefined): string | undefined {
	const value = unwrapParameterValue(parameter);
	if (typeof value !== 'string') {
		return undefined;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

/** Cleans every raw iCIMS location string, and drops the ones that hold no text. */
function unwrapLocations(
	parameter: UniformValueParameter<readonly unknown[]> | undefined,
): string[] {
	const value = unwrapParameterValue(parameter);
	if (!Array.isArray(value)) {
		return [];
	}
	return value
		.map((entry) => (typeof entry === 'string' ? formatIcimsLocation(entry) : undefined))
		.filter((entry): entry is string => entry !== undefined);
}

function unwrapParameterValue<T>(parameter: UniformValueParameter<T> | undefined): T | undefined {
	if (parameter === undefined) {
		return undefined;
	}
	if (typeof parameter !== 'object' || parameter === null) {
		return parameter as T;
	}
	if (Array.isArray(parameter)) {
		return parameter as T;
	}
	return (parameter as { value?: T }).value;
}

export default UniformJobPostingSubheading;
